import { tokens } from '@design';
// API Configuration
const getApiBaseUrl = () => {
  // Check if running in browser and on production domain
  if (typeof window !== 'undefined' && window.location) {
    if (window.location.hostname === 'sms.adaadev.ae') {
      return 'https://smsapi.adaadev.ae/api/v1';
    }
    if (window.location.hostname === 'newsms.adaadev.ae') {
      return 'https://newsmsapi.adaadev.ae/api/v1';
    }
    if (window.location.hostname === 'fujcci-strategy.adaadev.ae') {
      return 'https://fujcci-strategy-api.adaadev.ae/api/v1';
    }
  }
  return process.env.EXPO_PUBLIC_API_URL || 'http://localhost:8002/api/v1';
};

const getApiBaseHost = () => {
  // Check if running in browser and on production domain
  if (typeof window !== 'undefined' && window.location) {
    if (window.location.hostname === 'sms.adaadev.ae') {
      return 'https://smsapi.adaadev.ae';
    }
    if (window.location.hostname === 'newsms.adaadev.ae') {
      return 'https://newsmsapi.adaadev.ae';
    }
    if (window.location.hostname === 'fujcci-strategy.adaadev.ae') {
      return 'https://fujcci-strategy-api.adaadev.ae';
    }
  }
  return 'http://localhost:8002';
};

export const API_BASE_URL = getApiBaseUrl();
export const API_BASE_HOST = getApiBaseHost();

// SSO Service Configuration
const getSSOFrontendUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    if (window.location.hostname === 'sms.adaadev.ae') {
      return 'https://sso.adaadev.ae';
    }
    if (window.location.hostname === 'newsms.adaadev.ae') {
      return 'https://newsso.adaadev.ae';
    }
    if (window.location.hostname === 'fujcci-strategy.adaadev.ae') {
      return 'https://fujcci-sso.adaadev.ae';
    }
  }
  return process.env.EXPO_PUBLIC_SSO_FRONTEND_URL || 'http://localhost:3001';
};

export const SSO_FRONTEND_URL = getSSOFrontendUrl();

// SSO client_id — fetched at runtime from backend so it never needs to be baked in
let _ssoClientId: string = process.env.EXPO_PUBLIC_SSO_CLIENT_ID || 'sms';

export const loadSSOConfig = async (): Promise<void> => {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/config/`);
    if (res.ok) {
      const data = await res.json();
      if (data.client_id) _ssoClientId = data.client_id;
    }
  } catch {
    // Fall back to env/default
  }
};

export const getSSOClientId = () => _ssoClientId;

// Build the SSO login redirect URL
// SSO redirects back to app root with ?code=xxx, AuthContext middleware handles exchange
export const getSSOLoginUrl = () => {
  const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);
  return `${SSO_FRONTEND_URL}/login?client_id=${_ssoClientId}&redirect_uri=${redirectUri}`;
};

// App Configuration
export const APP_NAME = 'نظام إدارة الاستراتيجية';
export const APP_NAME_EN = 'Strategy Management System';

// Fonts - JF Flat font family
// Note: JF Flat only has Regular and Medium weights available
// Bold and SemiBold are aliased to Medium for consistency
export const FONTS = {
  regular: 'JF-Flat-Regular',
  medium: 'JF-Flat-Medium',
  semiBold: 'JF-Flat-Medium',  // Alias to Medium (no SemiBold variant available)
  bold: 'JF-Flat-Medium',      // Alias to Medium (no Bold variant available)
  // Segoe UI for numbers and percentages (system font)
  numbers: '"Segoe UI", system-ui, -apple-system, sans-serif',
};

// Typography System (font sizes only - uses JF Flat font)
export const TYPOGRAPHY = {
  // Headings
  h1: {
    fontSize: 48,
    
    lineHeight: 48, // 100%
  },
  h2: {
    fontSize: 30,
    lineHeight: 30, // 100%
  },
  h3: {
    fontSize: tokens.typography["heading 3"]["font size"],
    lineHeight: 29, // 120%
  },
  h4: {
    fontSize: tokens.typography["heading 4"]["font size"],
    lineHeight: 24, // 120%
  },
  // Paragraphs
  paragraph: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    lineHeight: 24, // 150%
  },
  paragraphSmall: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    lineHeight: 21, // 150%
  },
  // Mini text
  mini: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    lineHeight: 16, // 133%
  },
};

// Theme Colors - Based on SMS Design System
export const COLORS = {
  // Brand Primary
  primary: tokens.colors.semantic.general["Main Color"],
  primaryHover: tokens.colors.semantic.general["Main Dark"],
  primaryLight: tokens.colors.raw.indigo["50"],

  // Secondary (Blue - Links, Info)
  secondary: tokens.colors.raw.blue["500"],
  secondaryHover: '#2563EB',
  secondaryLight: '#EFF6FF',

  // Semantic Colors
  success: tokens.colors.raw.green["500"],
  successHover: '#16A34A',
  successLight: '#DCFCE7',

  warning: tokens.colors.raw.yellow["500"],
  warningHover: '#CA8A04',
  warningLight: '#FEF9C3',

  danger: tokens.colors.raw.red["500"],
  dangerHover: tokens.colors.raw.red["600"],
  dangerLight: tokens.colors.raw.red["50"],

  info: tokens.colors.raw.blue["500"],
  infoLight: '#DBEAFE',

  // Neutral Colors
  white: tokens.colors.raw.white,
  black: '#0A0A0A',

  // Neutral Scale (Design System)
  neutral50: '#FAFAFA',
  neutral100: '#F5F5F5',
  neutral200: '#E5E5E5',
  neutral300: '#D4D4D4',
  neutral400: '#A3A3A3',
  neutral500: '#737373',
  neutral600: '#525252',
  neutral700: '#404040',
  neutral800: '#262626',
  neutral900: '#171717',

  // Gray Scale (Tailwind - used across the app)
  gray50: tokens.colors.raw.gray["50"],
  gray100: tokens.colors.raw.gray["100"],
  gray200: tokens.colors.raw.gray["200"],
  gray300: tokens.colors.raw.gray["300"],
  gray400: tokens.colors.raw.gray["400"],
  gray500: tokens.colors.raw.gray["500"],
  gray600: '#4B5563',
  gray700: tokens.colors.raw.gray["700"],
  gray800: tokens.colors.raw.gray["800"],
  gray900: tokens.colors.raw.gray["900"],

  // Slate Scale (Tailwind - used for subtle backgrounds)
  slate100: tokens.colors.raw.slate["100"],
  slate200: tokens.colors.raw.slate["200"],
  slate300: tokens.colors.raw.slate["300"],
  slate400: '#94A3B8',
  slate500: tokens.colors.raw.slate["500"],
  slate600: '#475569',
  slate700: tokens.colors.raw.slate["700"],

  // Aliases for convenience
  background: '#FAFAFA',
  backgroundAlt: tokens.colors.raw.slate["100"],  // Subtle background
  card: tokens.colors.raw.white,
  cardBorder: tokens.colors.raw.gray["200"],     // Card border color
  border: '#E5E5E5',
  borderLight: tokens.colors.raw.slate["200"],    // Light border (Tailwind slate-200)
  inputBorder: '#D4D4D4',
  divider: tokens.colors.raw.gray["100"],        // Divider lines

  // Text Colors
  text: '#171717',
  textPrimary: tokens.colors.raw.gray["900"],    // Primary text (Tailwind gray-900)
  textSecondary: tokens.colors.semantic.general.foreground,  // Secondary text (common in app)
  textBody: '#525252',
  textMuted: tokens.colors.raw.gray["500"],      // Muted text (Tailwind gray-500)
  textLight: tokens.colors.raw.gray["400"],      // Light text (Tailwind gray-400)
  textDisabled: '#A3A3A3',

  // Icon Colors
  iconDefault: tokens.colors.semantic.general.foreground,    // Default icon color
  iconMuted: tokens.colors.raw.gray["500"],      // Muted icon color
  iconLight: tokens.colors.raw.gray["400"],      // Light icon color

  // Legacy aliases (for backward compatibility)
  light: '#F5F5F5',
  dark: '#262626',
  gray: '#737373',
  grayLight: '#E5E5E5',
  grayDark: '#525252',
};

// Status Colors
export const STATUS_COLORS: Record<string, string> = {
  success: COLORS.success,
  warning: COLORS.warning,
  danger: COLORS.danger,
  secondary: COLORS.gray,
};

// Initiative Status Colors
export const INITIATIVE_STATUS_COLORS: Record<string, string> = {
  planning: COLORS.info,
  in_progress: COLORS.primary,
  completed: COLORS.success,
  on_hold: COLORS.warning,
  cancelled: COLORS.danger,
};

// KPI Type Colors
export const KPI_TYPE_COLORS: Record<string, string> = {
  strategic: COLORS.primary,
  operational: COLORS.secondary,
};

// Arabic Labels
export const LABELS = {
  // Navigation
  dashboard: 'لوحة المعلومات',
  organizations: 'الجهات',
  goals: 'الأهداف الاستراتيجية',
  initiatives: 'المبادرات',
  kpis: 'مؤشرات الأداء',
  schedules: 'جدول القراءات',
  notifications: 'الإشعارات',
  settings: 'الإعدادات',
  profile: 'الملف الشخصي',
  logout: 'تسجيل الخروج',
  login: 'تسجيل الدخول',

  // Actions
  add: 'إضافة',
  edit: 'تعديل',
  delete: 'حذف',
  save: 'حفظ',
  cancel: 'إلغاء',
  search: 'بحث',
  filter: 'تصفية',
  refresh: 'تحديث',
  export: 'تصدير',
  import: 'استيراد',

  // Status
  loading: 'جاري التحميل...',
  error: 'حدث خطأ',
  success: 'تم بنجاح',
  noData: 'لا توجد بيانات',

  // KPI
  target: 'المستهدف',
  actual: 'الفعلي',
  achievement: 'نسبة الإنجاز',
  frequency: 'الدورية',
  unit: 'الوحدة',
  owner: 'المالك',

  // Initiative Status
  planning: 'تحت التخطيط',
  in_progress: 'قيد التنفيذ',
  completed: 'مكتملة',
  on_hold: 'معلقة',
  cancelled: 'ملغاة',

  // User Types
  strategy_team: 'فريق الاستراتيجية',
  org_employee: 'موظف جهة',
  executive: 'متخذ القرار',
};
